import React from 'react'
import { useState, useCallback } from 'react'
import { useNativeEvent } from '../DatePicker/FallbackDatePicker/useNativeEvent'
import { DatePicker } from 'components/inputs'
import { TimeSelect } from 'components/inputs/TimeSelect'
import { Option } from '../../FilterSelect/types'
import { useTranslation } from 'react-i18next'
import { WbControlHint, WbControlError } from '@workbench/react/dist'
import { Fieldset } from 'components/Layout/Fieldset'
import { FlexInputs } from 'components/Layout/FlexInputs'

interface DateTimePickerProps {
  id: string
  name: string
  dateLabel?: string
  datePattern?: string
  placeholder?: string
  timeLabel: string
  timeOptions: Option[]
  min?: string
  max?: string
  defaultValue?: string
  value: string
  hint?: string
  error?: string
}

type DateTime = {
  date: string
  time: string
}

export const convertDateTimeToString = ({ date, time }: DateTime): string =>
  date && time ? `${date}T${time}` : ''

export const convertStringToDateTime = (dateTimeString?: string): DateTime => {
  const parts: string[] = dateTimeString
    ? dateTimeString.split('T')
    : new Array<string>(2)

  return {
    date: parts[0] ?? '',
    time: parts[1] ?? '',
  }
}

export const DateTimePicker: React.FunctionComponent<
  DateTimePickerProps & JSX.IntrinsicElements['input']
> = ({
  id,
  name,
  dateLabel,
  datePattern,
  placeholder,
  timeLabel,
  timeOptions,
  min,
  max,
  value,
  defaultValue,
  hint,
  error,
  ...inputProps
}) => {
  const { t } = useTranslation()

  const initialDateStr = value || defaultValue || min || ''
  const initialDateTime = convertStringToDateTime(initialDateStr)

  const [touched, setTouched] = useState<boolean>(false)
  const touch = useCallback(() => setTouched(true), [setTouched])

  const [dateTime, setDateTime] = useState(initialDateTime)
  const dateTimeStr = convertDateTimeToString(dateTime)
  const inputRef = useNativeEvent(dateTimeStr, touched)

  const setDate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value: date } = e.target
      setDateTime((dt) => {
        return { ...dt, date }
      })
      touch()
    },
    [setDateTime, touch]
  )

  const setTime = useCallback(
    (time: string) => {
      setDateTime((dt) => {
        return { ...dt, time }
      })
      touch()
    },
    [setDateTime, touch]
  )
  return (
    <>
      <Fieldset>
        <FlexInputs style={{ alignItems: 'flex-end' }}>
          <input
            ref={inputRef}
            id={id}
            name={id}
            type="text"
            hidden
            {...inputProps}
            required={false}
            pattern={undefined}
            data-testid={id}
          />
          <DatePicker
            id={`${id}-date`}
            label={dateLabel}
            max={max}
            min={min}
            type="date"
            pattern={datePattern}
            placeholder={placeholder}
            value={dateTime.date}
            onChange={setDate}
          />
          <TimeSelect
            id={`${id}-time`}
            label={timeLabel}
            options={timeOptions.map((o: Option) => ({
              value: o.value,
              label: t(o.label),
            }))}
            value={dateTime.time}
            defaultValue={initialDateTime.time}
            onChange={setTime}
          />
        </FlexInputs>
      </Fieldset>
      {hint && (
        <WbControlHint
          style={{
            marginTop: '-.85rem',
            marginLeft: '.1rem',
            marginBottom: '.5rem',
          }}
        >
          {hint}
        </WbControlHint>
      )}
      {error && (
        <WbControlError
          style={{
            marginTop: '-.85rem',
            marginLeft: '.1rem',
            marginBottom: '.5rem',
          }}
        >
          {error}
        </WbControlError>
      )}
    </>
  )
}
